<script lang="ts">
    import vm, { JourneyNodeData } from '@journeys/Journeys.viewmodel';
    import { NodeTaskVariation, StepTypes, JourneyStatus } from '@journeys/Enums';
    import { IssueStatus } from '@cwd/Views/Issues/interfaces';
    import { TextMessageStatus } from '@cwd/Views/TextMessages/TextMessagesTypes.d';
    import Alert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';

    // assets
    import { api } from '@cwd/lib/main';

    // utils
    import { serializeStepSegments, stepList } from '@journeys/utils/journeySerialization';
    import BsAlert from '@shared/components/BsAlert.vue';

    const component: any = {
        name: 'Partial_TaskNodeSettings',
        fetched: {},
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    import { computed, ref, onMounted, reactive, watch } from 'vue';
    import Preview from '@shared/components/Preview.vue';
    import Modal from '@shared/components/Modal.vue';
    import SelectItemFromList from '@shared/components/SelectItemFromList.vue';
    import { ItemTypes } from '@shared/components/SelectItemFromList';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import ListItemSentStats from '@cwd/views/Issues/ListItemSentStats.vue';
    import TextMessageSentStats from '@cwd/views/TextMessages/List/Components/TextMessageSentStats.vue';
    import Loading from '@shared/components/Loading.vue';

    const { account, editor } = vm;
    const timeSpanSince = ref(0);
    const timeSpanSinceInvalid = ref(false);
    const previewUrl = ref<string | null>(null);

    const props = defineProps<{
        data: JourneyNodeData;
        id: number;
    }>();

    const handleTimeSpanSinceChange = (value?: string) => {
        if (!value) {
            timeSpanSince.value = 0;
            return;
        }
        timeSpanSince.value = +value;
        if (+value < 1) {
            timeSpanSinceInvalid.value = true;
        } else {
            timeSpanSinceInvalid.value = false;
            props.data.TimeSpanSince = +value;
        }
    };

    onMounted(() => {
        if (props.data.TimeSpanSince) {
            timeSpanSince.value = props.data.TimeSpanSince;
        }

        watch(
            () => props.data.resendToExistingRecipients,
            (newVal) => {
                if (newVal && !props.data.TimeSpanSince) {
                    props.data.TimeSpanSince = 30;
                    timeSpanSince.value = 30;
                } else if (!newVal) {
                    timeSpanSince.value = 0;
                    props.data.TimeSpanSince = null;
                }
            }
        );

        watch(
            () => props.data,
            (newVal) => {
                if (newVal.TimeSpanSince) {
                    timeSpanSince.value = newVal.TimeSpanSince;
                    if (+timeSpanSince.value < 30) {
                        timeSpanSinceInvalid.value = true;
                    } else {
                        timeSpanSinceInvalid.value = false;
                    }
                }
            }
        );

        watch(() => [props.data.IssueId, props.data.SmsMessageId], fetchStats, { immediate: true });
    });

    const state = reactive({
            page: 0,
            pageSize: 20,
            showSelectorModal: false,
            loadingStatistics: false,
        }),
        getListingEndpoint = (): string => {
            switch (props.data.var) {
                case NodeTaskVariation.Survey:
                    return '/surveys/listing';
                case NodeTaskVariation.Sms:
                    return '/textmessages/listing';
                case NodeTaskVariation.Event:
                    return '/events/listing';
                default:
                    return '/issues/listing';
            }
        },
        fetchLatest = (): any => account.listings.get(getListingEndpoint()),
        isLoadingListings = computed(() => account.listings.catalog[getListingEndpoint()]?.status === 'pending'),
        filteredListings = computed(() => {
            let listings = fetchLatest();

            if (!Array.isArray(listings)) return;

            const journeyId = vm.activeJourney.JourneyId;
            const usedIssueIds = new Set<string>();
            const usedSmsIds = new Set<string>();

            Object.values(vm.activeJourney.nodes).forEach((node: any) => {
                if (!node || node.id === props.id) return;

                const nodeData = node.data;
                if (!nodeData) return;

                if (nodeData.IssueId) {
                    usedIssueIds.add(nodeData.IssueId);
                }
                if (nodeData.SmsMessageId) {
                    usedSmsIds.add(nodeData.SmsMessageId);
                }
            });

            if (props.data.var === NodeTaskVariation.Email) {
                listings = listings.filter((listing: any) => {
                    if (listing.Status === IssueStatus.Template) return false;

                    // An email owned by an event (confirmation, reminder, etc.) belongs to that event and cannot be sent from a journey.
                    if (listing.Event) return false;

                    if (listing.IssueId && usedIssueIds.has(listing.IssueId) && listing.IssueId !== props.data.IssueId) {
                        return false;
                    }

                    return listing.Journey === null || listing.Journey.JourneyId === journeyId;
                });
            }

            if (props.data.var === NodeTaskVariation.Sms) {
                listings = listings.filter((listing: any) => {
                    if (listing.Status === TextMessageStatus.Template) return false;
                    return !listing.SmsMessageId || !usedSmsIds.has(listing.SmsMessageId) || listing.SmsMessageId === props.data.SmsMessageId;
                });
            }

            listings = listings.reduce((acc: any[], item: any, idx: number) => {
                acc.push(item);

                return acc;
            }, []);

            // return listings
            return [...listings]; // temp sorting fix
        });

    const selectedObjectStats = ref(null);

    const fetchStats = async () => {
        state.loadingStatistics = true;

        if (props.data.var === NodeTaskVariation.Email && props.data.IssueId) {
            const stats = await account.listings.getStatistics(props.data.IssueId);
            state.loadingStatistics = false;
            selectedObjectStats.value = stats ? { ...stats } : null;
        } else if (props.data.var === NodeTaskVariation.Sms && props.data.SmsMessageId) {
            const stats = await account.listings.getTextMessageStatistics(props.data.SmsMessageId);
            state.loadingStatistics = false;
            selectedObjectStats.value = stats ? { ...stats } : null;
        } else {
            selectedObjectStats.value = null;
        }
    };

    const selectedObject = computed(() => {
        if (props.data.var === NodeTaskVariation.Email && props.data.IssueId) {
            return account.listings.getListing('/issues/listing', props.data.IssueId);
        }
        if (props.data.var === NodeTaskVariation.Sms && props.data.SmsMessageId) {
            return account.listings.getListing('/textmessages/listing', props.data.SmsMessageId);
        }
        if (props.data.var === NodeTaskVariation.Survey && props.data.SurveyId) {
            return account.listings.getListing('/surveys/listing', props.data.SurveyId);
        }
        if (props.data.var === NodeTaskVariation.Event && props.data.EventId) {
            return account.listings.getListing('/events/listing', props.data.EventId);
        }

        return null;
    });

    const updateId = (data: any, newId: string) => {
        state.showSelectorModal = false;

        switch (data.var) {
            case NodeTaskVariation.Event:
                return (data.EventId = newId);
            case NodeTaskVariation.Survey:
                return (data.SurveyId = newId);
            case NodeTaskVariation.Sms:
                return (data.SmsMessageId = newId);
            default:
                return (data.IssueId = newId);
        }
    };

    const getType = (type: number): ItemTypes => {
        switch (type) {
            case NodeTaskVariation.Event:
                return ItemTypes.Events;
            case NodeTaskVariation.Survey:
                return ItemTypes.Surveys;
            case NodeTaskVariation.Sms:
                return ItemTypes.Sms;
            default:
                return ItemTypes.Issues;
        }
    };

    const isTemplate = computed(() => {
        if (props.data.var === NodeTaskVariation.Email && selectedObject.value?.Status === IssueStatus.Template) {
            return true;
        }
        if (props.data.var === NodeTaskVariation.Sms && selectedObject.value?.Status === TextMessageStatus.Template) {
            return true;
        }
        return false;
    });

    const journeyIsDraft = computed(() => vm.activeJourney.Status === JourneyStatus.Draft);

    const openSendLog = (issueId: string) => {
        editor.state.sendLogIssueFilter = issueId;
        editor.state.showSendLogModal = true;
    };

    const openSmsLog = (smsMessageId: string) => {
        editor.state.sendLogSmsFilter = smsMessageId;
        editor.state.showSendLogModal = true;
    };

    const isOwnedByOtherJourney = computed(() => {
        if (!selectedObject.value) return false;

        const journey = selectedObject.value.Journey;
        if (!journey) return false;

        if (props.data.var === NodeTaskVariation.Email || props.data.var === NodeTaskVariation.Sms) {
            return journey.JourneyId !== vm.activeJourney.JourneyId;
        }
        return false;
    });

    const isOwnedByEvent = computed(() => props.data.var === NodeTaskVariation.Email && !!selectedObject.value?.Event);
</script>

<template>
    <div class="m-lg-b">
        <BsAlert :show="isTemplate" :type="AlertTypes.Danger" :content="$Resources.JourneyAlertNodeConnectedToTemplate" :dismissible="false" />

        <BsAlert :show="isOwnedByOtherJourney" :type="AlertTypes.Danger" :content="$Resources.JourneyAlertIssueOwnedByOtherJourney" :dismissible="false" />

        <BsAlert :show="isOwnedByEvent" :type="AlertTypes.Danger" :content="$Resources.JourneyAlertIssueOwnedByEvent" :dismissible="false" />

        <template v-if="data.var === NodeTaskVariation.Email">
            <template v-if="selectedObject">
                <h3 class="h5">{{ $Resources.SelectedEmailLabel }}:</h3>

                <div class="node-card">
                    <h4 class="item-heading">
                        <a :href="`/Issues/${selectedObject.IssueId}/Statistics`" target="_blank" rel="noopener">
                            {{ selectedObject.DynamicSubject || selectedObject.Subject || $Resources.NoTitleLabel }}
                            <small class="glyphicon glyphicon glyphicon-new-window m-xxs-l m-xs-r" aria-hidden="true"></small>
                        </a>

                        <small>{{ selectedObject.Category?.Name }}</small>

                        <button
                            type="button"
                            class="btn btn-subtle btn-xs"
                            v-tooltip.top="$Resources.IssuePreviewButton"
                            @click="previewUrl = `${api.settings.publicHost}/Issues/${selectedObject.IssueId}?preview=true`">
                            <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                            <span class="sr-only">{{ $Resources.PreviewButton }}</span>
                        </button>
                    </h4>

                    <p v-if="selectedObject.IssueName">
                        {{ selectedObject.IssueName }}
                    </p>

                    <ListItemSentStats
                        class="flex-even m-sm-b"
                        :loading="state.loadingStatistics"
                        :issueStatistics="selectedObjectStats"
                        :sentLabel="$Resources.JourneyListStatsContactsLabel"
                        :receivedLabel="$Resources.JourneyListStatsRecipientsLabel"
                        :showReceivedAsCount="true"
                        :showExactCounts="true" />

                    <TagsList v-if="selectedObject.Tags && selectedObject.Tags.length" :tags="selectedObject.Tags" :readonly="true"></TagsList>

                    <p class="item-meta d-flex gap-xs">
                        <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px">
                            <path
                                d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h357l-80 80H200v560h560v-278l80-80v358q0 33-23.5 56.5T760-120H200Zm280-360ZM360-360v-170l367-367q12-12 27-18t30-6q16 0 30.5 6t26.5 18l56 57q11 12 17 26.5t6 29.5q0 15-5.5 29.5T897-728L530-360H360Zm481-424-56-56 56 56ZM440-440h56l232-232-28-28-29-28-231 231v57Zm260-260-29-28 29 28 28 28-28-28Z" />
                        </svg>
                        <span>
                            {{ $Resources.LastModifiedLabel }}
                            <time :datetime="selectedObject.LastModified">{{ formatDateTime(selectedObject.LastModified) }}</time>
                            {{ $Resources.GenericByLabel }}
                            {{ selectedObject.LastModifiedBy?.Name }}
                        </span>
                    </p>
                </div>
            </template>

            <div class="form-group d-flex gap-sm flex-wrap justify-content-between">
                <button class="btn btn-default" @click="state.showSelectorModal = true">
                    {{ $Resources.NodeTaskChooseIssue }}
                </button>

                <button v-if="data.IssueId" class="btn btn-subtle" @click="openSendLog(data.IssueId)">
                    {{ $Resources.JourneySendLogOpenLogBtn }}
                </button>
            </div>

            <h3 class="h4">{{ $Resources.AllowResendableIssueLabel }}</h3>

            <div class="form-group">
                <FormCheckable
                    :switch-style="SwitchStyle.Standard"
                    :label="$Resources.YesLabel"
                    :checked="!!data.resendToExistingRecipients"
                    @update:modelValue="(val: any) => (data.resendToExistingRecipients = !!val)" />
            </div>

            <FormInput
                v-if="data.resendToExistingRecipients"
                class="form-group"
                :class="{ 'has-error': timeSpanSinceInvalid }"
                type="number"
                :label="$Resources.AllowResendableIssueTimeInterval"
                :placeholder="$Resources.SegmentPropertyValuePlaceholder"
                :model-value="timeSpanSince"
                @update:modelValue="(val: any) => (timeSpanSince = +val)"
                @onBlur="() => handleTimeSpanSinceChange(timeSpanSince?.toString())" />
        </template>

        <template v-else-if="data.var === NodeTaskVariation.Sms">
            <template v-if="selectedObject">
                <h3 class="h5">{{ $Resources.SelectedSmsLabel }}:</h3>

                <div class="node-card">
                    <h4 class="item-heading">
                        <a :href="`/TextMessages/${selectedObject.SmsMessageId}/Statistics`" target="_blank" rel="noopener">
                            {{ selectedObject.Subject || $Resources.NoTitleLabel }}
                            <small class="glyphicon glyphicon glyphicon-new-window m-xxs-l m-xs-r" aria-hidden="true"></small>
                        </a>

                        <small>{{ selectedObject.Category?.Name }}</small>

                        <button
                            type="button"
                            class="btn btn-subtle btn-xs"
                            v-popover-info.top="{
                                heading: $Resources.IssuePreviewButton,
                                body: selectedObject.Body,
                                displayHtml: true,
                            }">
                            <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                            <span class="sr-only">{{ $Resources.IssuePreviewButton }}</span>
                        </button>
                    </h4>

                    <TagsList v-if="selectedObject.Tags && selectedObject.Tags.length" :tags="selectedObject.Tags" :readonly="true"></TagsList>

                    <TextMessageSentStats
                        class="flex-even m-sm-b"
                        :textMessage="selectedObjectStats"
                        :loading="state.loadingStatistics"
                        :sentLabel="$Resources.JourneyListStatsContactsLabel"
                        :receivedLabel="$Resources.JourneyListStatsRecipientsLabel"
                        :showReceivedAsCount="true"
                        :showExactCounts="true" />

                    <p class="item-meta d-flex gap-xs">
                        <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px">
                            <path
                                d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h357l-80 80H200v560h560v-278l80-80v358q0 33-23.5 56.5T760-120H200Zm280-360ZM360-360v-170l367-367q12-12 27-18t30-6q16 0 30.5 6t26.5 18l56 57q11 12 17 26.5t6 29.5q0 15-5.5 29.5T897-728L530-360H360Zm481-424-56-56 56 56ZM440-440h56l232-232-28-28-29-28-231 231v57Zm260-260-29-28 29 28 28 28-28-28Z" />
                        </svg>
                        <span>
                            {{ $Resources.LastModifiedLabel }}
                            <time :datetime="selectedObject.Modified">{{ formatDateTime(selectedObject.Modified) }}</time>
                            {{ $Resources.GenericByLabel }}
                            {{ selectedObject.ModifiedBy?.Name }}
                        </span>
                    </p>
                </div>
            </template>

            <div class="form-group d-flex gap-sm flex-wrap justify-content-between">
                <button class="btn btn-default" @click="state.showSelectorModal = true">
                    {{ $Resources.NodeTaskChooseSms }}
                </button>

                <button v-if="data.SmsMessageId" class="btn btn-subtle" @click="openSmsLog(data.SmsMessageId)">
                    {{ $Resources.JourneySendLogOpenLogBtn }}
                </button>
            </div>

            <div class="form-group">
                <h3 class="h4 m-0-t item-heading">{{ $Resources.AllowResendableSmsLabel }}</h3>
                <FormCheckable
                    :switch-style="SwitchStyle.Standard"
                    :label="$Resources.YesLabel"
                    :checked="!!data.resendToExistingRecipients"
                    @update:modelValue="(val: any) => (data.resendToExistingRecipients = !!val)" />
            </div>

            <FormInput
                v-if="data.resendToExistingRecipients"
                class="form-group"
                :class="{ 'has-error': timeSpanSinceInvalid }"
                type="number"
                :label="$Resources.AllowResendableSmsTimeInterval"
                :placeholder="$Resources.SegmentPropertyValuePlaceholder"
                :model-value="timeSpanSince"
                @update:modelValue="(val: any) => handleTimeSpanSinceChange(val)" />
        </template>
    </div>

    <Modal :title="$Resources.ChooseHeading" :show="state.showSelectorModal" @close="state.showSelectorModal = false" size="lg">
        <template #content>
            <SelectItemFromList
                v-if="filteredListings && data && data.var"
                :items="filteredListings"
                :type="getType(data.var)"
                @update:selectedId="(newId: string) => updateId(data, newId)"></SelectItemFromList>

            <Loading :show="isLoadingListings" :fixed="true" />
        </template>
    </Modal>

    <Preview :previewUrl="previewUrl" @close="previewUrl = null" />
</template>

<style>
    .node-card {
        display: flex;
        flex-direction: column;
        background: var(--brand-secondary-50);
        padding: var(--ug-spacing-xs) var(--ug-spacing-lg) var(--ug-spacing-sm);
        margin-bottom: var(--ug-spacing-md);
        border-radius: calc(var(--border-radius) * 2);
        border: 1px solid rgba(0, 0, 0, 0.1);
    }
</style>
